/**
 * useMediaQueryFlag — the body every boolean media-query hook in here shared.
 *
 * {@link ./useIsPortrait}, {@link ./useIsNarrowPhone} and
 * {@link ./useIsShortViewport} are the same twenty lines three times over, and
 * the parts that are easy to get wrong are exactly the parts that were copied:
 * the lazy initial state (a `useState(false)` corrected by the effect is a
 * visible flash of the wrong layout), the re-sync before subscribing (a
 * viewport that moved between render and effect is otherwise missed until the
 * next change event), the Safari < 14 fallback, and a teardown symmetric with
 * whichever subscribe API was used.
 *
 * Each hook keeps its own exported query constant and its own synchronous
 * `matches*()` reader: those are what a `node`-env test and a first paint can
 * call without React, and what pins the query itself.
 *
 * {@link ./useIsMobile} and {@link ./useViewportTier} deliberately stay as they
 * are — the first is load-bearing for the squeeze compat layer and the second
 * subscribes to two queries at once, so neither is this shape.
 */

import { useEffect, useState } from 'react';
// The pristine matchMedia, for the same reason useIsMobile takes it: the
// sidebar-compat viewport facade rewrites queries for the HOST page, and our
// own layout must read the real viewport (see ../sdk/native-viewport).
import { realMatchMedia } from '../sdk/native-viewport';

/**
 * Subscribe to a MediaQueryList, returning its teardown.
 *
 * `addEventListener` is the modern API; `addListener` is the deprecated
 * fallback for older Safari (< 14) where the former is missing. The teardown
 * has to match the one that was used — removing with the other API leaves the
 * listener attached to a dead component.
 *
 * A MediaQueryList with NEITHER is not hypothetical here: this runs on customer
 * pages, and a host shim that returns `{ matches }` for `matchMedia` is a shape
 * we have to survive. Calling through it would throw inside the effect, which
 * React escalates past our own boundaries and takes the whole overlay down —
 * over a query. So an unsubscribable list degrades to the static first-paint
 * read: the flag simply stops following the viewport.
 */
export function subscribeMediaQuery(mql: MediaQueryList, onChange: () => void): () => void {
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }
  // The deprecated pair is taken only if BOTH halves are there. Subscribing
  // with `addListener` and tearing down through an optional `removeListener`
  // would leave a live listener pointed at an unmounted component whenever the
  // shim implements one and not the other — a silent leak that fires setState
  // forever. Half an API is no API.
  if (typeof mql.addListener === 'function' && typeof mql.removeListener === 'function') {
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }
  return () => {};
}

/**
 * Track one media query as a boolean, live.
 *
 * `matches` is the caller's own synchronous reader, used for the lazy initial
 * state so the first paint is already correct. `query` is a module constant in
 * every caller, so the effect runs once per mount; it is a dependency anyway,
 * because a helper that takes a query and ignores changes to it is a stale
 * subscription waiting for the first caller that computes one.
 */
export function useMediaQueryFlag(query: string, matches: () => boolean): boolean {
  // Both reads are wrapped: the threat model above is a host page's own
  // matchMedia shim, and one that THROWS would take the overlay down from
  // inside a render or an effect, where React escalates past our boundaries.
  // `false` and no subscription is the degraded answer every caller can live
  // with — see each hook's `matches*()` for why that default is the safe one.
  const [flag, setFlag] = useState<boolean>(() => {
    try {
      return matches();
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mql: MediaQueryList | null = null;
    try {
      mql = realMatchMedia(query);
    } catch {
      return;
    }
    if (!mql) return;
    // A const alias, so the closure below cannot lose the narrowing.
    const list = mql;
    const onChange = () => setFlag(list.matches);
    // Re-sync once on mount: the viewport may have changed between the
    // initial render and this effect running.
    onChange();
    return subscribeMediaQuery(list, onChange);
  }, [query]);

  return flag;
}
